import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cl from 'classnames';
import { OfflineHeadset } from 'src/models';
import { ALL_ITEM_ID } from 'src/constants';
import { useContextData, useLanguage } from 'src/hooks';
import {
  compareString,
  formatMskShortDate,
  formatWorkingMode,
  getLastActivityColor,
  hybridCompare,
} from 'src/helper';
import { useAppDispatch } from 'src/store';
import { clearOfflineHeadsetsState } from 'src/store/reducers/offlineHeadsets';
import { useAccount, useAccountState } from 'src/services/auth';
import {
  getOfflineHeadsetsAction,
  useOfflineHeadsets,
  useOfflineHeadsetsState,
  GetOfflineHeadsetsQueryParams,
} from 'src/services/offlineHeadsets';
import { PageSwitcher, StickyTable, StickyTableConfig } from 'src/components';
import {
  GroupFilter,
  Tooltip,
  Button,
  useDateRangePicker,
} from 'src/components/general';
import { ReactComponent as TooltipIcon } from 'src/icons/tooltip.svg';
import styles from './reportsOfflineHeadsetsStyle.module.scss';
import CopySelect from './copySelect';

export const ReportsOfflineHeadsets = () => {
  const {
    header: { width: headerWidth },
  } = useContextData();
  const dispatch = useAppDispatch();
  const { user } = useAccountState();
  const { isPending } = useOfflineHeadsetsState();
  const { t, td } = useLanguage();
  const headsets = useOfflineHeadsets();
  const account = useAccount();

  const { range, dateRangePicker } = useDateRangePicker({
    inputClassName: styles.datepickerInput,
    disabled: isPending,
    withoutClear: true,
  });

  useEffect(
    () => () => {
      dispatch(clearOfflineHeadsetsState());
    },
    [dispatch],
  );

  const companies = useMemo(() => user?.companies ?? [], [user?.companies]);
  const [selectedCompany, setSelectedCompany] = useState<Set<string>>(
    new Set(),
  );

  const companiesList = useMemo(
    () =>
      companies.map((item) => ({
        id: item.id,
        label: item.name,
      })),
    [companies],
  );

  const config: StickyTableConfig<OfflineHeadset>[] = useMemo(
    () => [
      {
        title: t('page.reports.headsets.label'),
        key: 'label',
        field: 'label',
        sorter: (a, b, sortOrder) =>
          hybridCompare(a.label, b.label) * (sortOrder === 'descend' ? -1 : 1),
        render: (label, headset) => {
          return (
            <div className={styles.label}>
              <span>{label || '-'}</span>{' '}
              <div className={styles.secondary}>
                {headset.firmware || '-'}
                {'-'}
                {headset.deviceType || t('general.unknown')}
                <Tooltip
                  text={[
                    t('tooltip.headsetId', { id: headset.phyDeviceId }),
                    t('tooltip.humanId', { id: headset.humanId }),
                  ]}
                  tooltipClassName={styles.tooltip}
                  arrowClassName={styles.tooltipArrow}
                  withClipboard
                >
                  <TooltipIcon />
                </Tooltip>
              </div>
            </div>
          );
        },
      },
      {
        title: t('page.reports.headsets.company'),
        key: 'companyName',
        field: 'companyName',
        sorter: (a, b, sortOrder) =>
          compareString(a.companyName, b.companyName) *
          (sortOrder === 'descend' ? -1 : 1),
      },
      {
        title: t('page.reports.headsets.workingMode'),
        key: 'workingMode',
        field: 'workingMode',
        sorter: (a, b, sortOrder) =>
          compareString(a.workingMode, b.workingMode) *
          (sortOrder === 'descend' ? -1 : 1),
        render: (value) => <div>{td(formatWorkingMode(value))}</div>,
      },
      {
        title: t('page.reports.headsets.roomLabel'),
        key: 'roomLabel',
        field: 'roomLabel',
        sorter: (a, b, sortOrder) =>
          compareString(a.roomLabel, b.roomLabel) *
          (sortOrder === 'descend' ? -1 : 1),
        render: (value) => (
          <div className={styles.roomLabel}>{value || '-'}</div>
        ),
      },
      {
        title: t('page.reports.headsets.lastActivity'),
        key: 'lastActivity',
        field: 'lastActivity',
        sorter: (a, b, sortOrder) => {
          const dateA = new Date(a.lastActivity).getTime();
          const dateB = new Date(b.lastActivity).getTime();
          return (dateA - dateB) * (sortOrder === 'descend' ? -1 : 1);
        },
        render: (value, { alive }) =>
          alive ? (
            <div className={cl(styles.tag, styles.tag120, styles.green)}>
              {t('page.reports.headsets.online')}
            </div>
          ) : (
            <div
              className={cl(
                styles.tag,
                styles.tag120,
                styles[getLastActivityColor(value)],
              )}
            >
              {formatMskShortDate(value)}
            </div>
          ),
      },
    ],
    [t, td],
  );

  const selectCompany = useCallback(
    (item: string, value: boolean, selected: Set<string>) => {
      if (item === ALL_ITEM_ID) {
        return selected.size - (selected.has(ALL_ITEM_ID) ? 1 : 0) <
          companies.length
          ? new Set([ALL_ITEM_ID, ...companies.map((item) => item.id)])
          : new Set<string>();
      }

      const updatedIDs = new Set(selected);
      updatedIDs.delete(ALL_ITEM_ID);

      if (value) {
        updatedIDs.add(item);
      } else {
        updatedIDs.delete(item);
      }

      if (updatedIDs.size === companies.length) {
        updatedIDs.add(ALL_ITEM_ID);
      }

      return updatedIDs;
    },
    [companies],
  );

  const apply = useCallback(() => {
    if (isPending || !range.start || !range.end || !selectedCompany.size) {
      return;
    }

    const params: GetOfflineHeadsetsQueryParams = {
      companies:
        !selectedCompany.size || selectedCompany.has(ALL_ITEM_ID)
          ? undefined
          : [...selectedCompany].join(','),
      start: range.start.format,
      end: range.end.format,
    };

    dispatch(getOfflineHeadsetsAction({ params }));
  }, [dispatch, isPending, selectedCompany, range.start, range.end]);

  const emptyCell = useMemo(
    () => <>{t('page.reports.headsets.emptyCell')}</>,
    [t],
  );

  return (
    <div className={styles.container}>
      <div
        className={styles.topSection}
        style={{
          minWidth: headerWidth,
          maxWidth: headerWidth,
        }}
      >
        <div className={styles.pageSwitcherContainer}>
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
        </div>
        <div className={styles.filtersWrapper}>
          <div className={styles.filters}>
            <GroupFilter
              label={t('general.selectedCompanies')}
              selectedIDs={selectedCompany}
              items={companiesList}
              selectItem={(item, value) => {
                setSelectedCompany(selectCompany(item, value, selectedCompany));
              }}
              className={styles.selectCompany}
              disabled={Boolean(isPending)}
            />
            {dateRangePicker}
            <Button
              onClick={() => {
                apply();
              }}
              label={t('general.apply')}
              design="white"
              size="middle"
              className={styles.actionButton}
              disabled={Boolean(isPending) || !selectedCompany.size}
            />
            <CopySelect
              headsets={headsets}
              className={styles.copyButton}
              // isDisabled={!headsets.length}
            />
          </div>
        </div>
      </div>
      <StickyTable
        config={account ? config : null}
        data={headsets}
        withPaginator
        isLoading={isPending}
        className={styles.table}
        emptyCell={emptyCell}
      />
    </div>
  );
};

export default ReportsOfflineHeadsets;
